/* =====================================================================
   The Italian Chocolate 1971 · cart drawer extras behaviour       v2 · 2026-10-15
   Theme: Monochrome 1.2.2 (Dawn-based cart drawer). DRAFT theme only.
   Load once from layout/theme.liquid, right before </head> (see site/patches.md):
     <script src="{{ 'tic-cart.js' | asset_url }}" defer="defer"></script>
   Markup: snippets/tic-cart-extras.liquid (anchors A 'top' and B 'footer' of the drawer).

   What it does
   1. Progress bar / icon / message: the theme re-renders .drawer__inner from Liquid after every
      add and quantity change, so the new numbers arrive as fresh DOM. A MutationObserver on
      <cart-drawer> spots the new [data-tic-ship] node, puts it back in the PREVIOUS state without
      transitions, then switches to the new state so the bar fills (scaleX, 300ms ease-out) and the
      truck/check icons cross-fade (better-ui recipe). No animation on page load.
   2. Items enter with a 40ms stagger only when the drawer opens by pointer (never on keyboard
      open, never on a re-render while open).
   3. "Add" in "Complete the collection": POST /cart/add.js with the drawer's own sections, then the
      theme's renderContents() (falls back to /cart if the theme API is missing or on error).
   4. Safety net: on the theme's `cart:change` event, the pub/sub `cart-update` and `cart:refresh`,
      if the extras still show an old total (e.g. an app changed the cart without re-rendering
      the drawer), the three parts are re-fetched with the Section Rendering API and replaced
      by key (data-tic-cx), so they can never be duplicated.
   ===================================================================== */
(function () {
  'use strict';
  if (window.__ticCartExtras) return; // included twice: keep a single instance
  window.__ticCartExtras = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var state = { fill: null, unlocked: null, msgA: '' };
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
  var lastInput = 'pointer';
  var staggerTimer = null;
  var syncTimer = null;
  var pendingTotal = null;
  var subscribed = false;

  function reduced() { return !!(mqReduce && mqReduce.matches); }
  function drawerEl() { return document.querySelector('cart-drawer'); }
  function isOpen(d) { return !!(d && d.classList.contains('active')); }
  function clamp01(n) { return n < 0 ? 0 : n > 1 ? 1 : n; }
  function markSeen(el) {
    if (seen) { if (seen.has(el)) return false; seen.add(el); return true; }
    if (el.__ticSeen) return false; el.__ticSeen = true; return true;
  }

  // Remember how the last interaction happened (keyboard actions are not animated).
  document.addEventListener('keydown', function (e) {
    if (!e.metaKey && !e.ctrlKey && !e.altKey) lastInput = 'key';
  }, true);
  document.addEventListener('pointerdown', function () { lastInput = 'pointer'; }, true);
  // iOS Safari applies :active (the 0.96 press scale on checkout / Add / Apply) only when a
  // touch listener exists somewhere on the page.
  document.addEventListener('touchstart', function () {}, { passive: true });

  /* ---------- 1. progress: previous state -> new state ---------- */
  function enhanceShip(el, animate) {
    if (!markSeen(el)) return;
    var fill = clamp01(parseFloat(el.getAttribute('data-fill')) || 0);
    var unlocked = el.getAttribute('data-unlocked') === 'true';
    var msgA = el.querySelector('[data-tic-msg-a]');
    var bar = el.querySelector('.tic-ship__fill');
    var from = state.fill;
    var fromUnlocked = state.unlocked;
    var prevMsg = state.msgA;

    state.fill = fill;
    state.unlocked = unlocked;
    if (!unlocked && msgA && msgA.innerHTML.trim()) state.msgA = msgA.innerHTML;

    if (!animate || from === null || !bar || reduced()) return;
    if (from === fill && fromUnlocked === unlocked) return;

    // Unlocking: the "You're X away" line fades out with the wording it had a moment ago.
    if (unlocked && !fromUnlocked && msgA && prevMsg && !msgA.innerHTML.trim()) msgA.innerHTML = prevMsg;

    var d = drawerEl();
    var delay = isOpen(d) ? 0 : 180; // drawer is about to slide in: start once it has nearly landed
    var iconDelay = unlocked !== fromUnlocked && unlocked ? delay + 150 : delay; // check appears as the bar fills

    el.classList.add('is-instant');
    el.classList.toggle('is-unlocked', !!fromUnlocked);
    bar.style.transform = 'scaleX(' + from + ')';
    void el.offsetWidth; // commit the previous state
    el.classList.remove('is-instant');
    el.style.setProperty('--tic-delay', delay + 'ms');
    el.style.setProperty('--tic-delay-icon', iconDelay + 'ms');
    requestAnimationFrame(function () {
      el.classList.toggle('is-unlocked', unlocked);
      bar.style.transform = 'scaleX(' + fill + ')';
    });
  }

  function scan(d, animate) {
    var ships = d.querySelectorAll('[data-tic-ship]');
    for (var i = 0; i < ships.length; i++) enhanceShip(ships[i], animate);
    if (!ships.length && !d.querySelector('[data-tic-cx="top"]')) {
      // empty cart (or the drawer has no extras): the next add starts from 0
      state.fill = 0;
      state.unlocked = false;
    }
  }

  /* ---------- 2. stagger on open ---------- */
  function onOpen(d) {
    if (lastInput === 'key') return;
    var items = d.querySelectorAll('#CartDrawer-CartItems .cart-item, .tic-upsell');
    for (var i = 0; i < items.length; i++) items[i].style.setProperty('--tic-i', String(Math.min(i, 8)));
    d.classList.remove('tic-stagger');
    void d.offsetWidth;
    d.classList.add('tic-stagger');
    // the class goes once the last item has finished (or after 1.5s), so a re-render while the
    // drawer is open never replays the entrance
    // animationend bubbles: only the last item's OWN entrance counts (a spinner or any child
    // animation ending first would otherwise cut the stagger short and make the items jump)
    var last = items.length ? items[items.length - 1] : null;
    var onEnd = function (e) { if (e.target === last) done(); };
    var done = function () {
      clearTimeout(staggerTimer);
      if (last) last.removeEventListener('animationend', onEnd);
      d.classList.remove('tic-stagger');
    };
    clearTimeout(staggerTimer);
    staggerTimer = setTimeout(done, 1500);
    if (last) last.addEventListener('animationend', onEnd);
  }

  /* ---------- 3. "Add" in Complete the collection ---------- */
  function showError(btn, message) {
    var box = btn.closest('.tic-upsell');
    var out = box && box.querySelector('[data-tic-error]');
    btn.removeAttribute('aria-busy');
    btn.removeAttribute('aria-disabled');
    if (out) {
      out.textContent = message || 'This box could not be added. Please try again.';
      out.hidden = false;
    }
  }

  function addVariant(btn) {
    var d = drawerEl();
    var id = Number(btn.getAttribute('data-tic-add'));
    if (!id) return;
    var viaKey = lastInput === 'key';
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    try {
      if (d && typeof d.getSectionsToRender === 'function') {
        sections = d.getSectionsToRender().map(function (s) { return s.id; });
      }
    } catch (err) { /* keep defaults */ }

    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('aria-disabled', 'true');
    fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: sections, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
      .then(function (res) {
        if (!res.ok || res.body.status) {
          showError(btn, res.body && (res.body.description || res.body.message));
          return null;
        }
        if (!d || typeof d.renderContents !== 'function' || !res.body.sections) {
          window.location.href = ROOT + 'cart';
          return null;
        }
        d.renderContents(res.body);
        // keep focus inside the dialog: the new line for keyboard users, the panel otherwise
        requestAnimationFrame(function () {
          var target = viaKey ? d.querySelector('#CartDrawer-CartItems .cart-item a[href]') : null;
          target = target || d.querySelector('.drawer__inner');
          if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
        });
        return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart, sectionId: 'tic-cart-extras' } }));
          });
      })
      .catch(function () { window.location.href = ROOT + 'cart'; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-tic-add]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    addVariant(btn);
  });

  /* ---------- 4. safety net: refresh stale extras, replace by key ---------- */
  function insertPart(d, key, node) {
    if (key === 'top') {
      var before = d.querySelector('.drawer__inner-empty') || d.querySelector('cart-drawer-items');
      if (before && before.parentNode) before.parentNode.insertBefore(node, before);
    } else {
      var totals = d.querySelector('.cart-drawer__footer .totals');
      if (totals && totals.parentNode) totals.parentNode.insertBefore(node, totals);
    }
  }

  function replaceParts(d, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    ['top', 'upsell', 'trust'].forEach(function (key) {
      var fresh = doc.querySelector('[data-tic-cx="' + key + '"]');
      var olds = d.querySelectorAll('[data-tic-cx="' + key + '"]');
      for (var i = 1; i < olds.length; i++) olds[i].remove(); // never more than one
      var old = olds[0];
      if (fresh) {
        var node = document.importNode(fresh, true);
        if (old) old.replaceWith(node); else insertPart(d, key, node);
      } else if (old) {
        old.remove();
      }
    });
  }

  function sync(total) {
    var d = drawerEl();
    if (!d) return;
    var live = d.querySelector('[data-tic-cx]');
    var liveTotal = live ? Number(live.getAttribute('data-tic-total')) : null;
    var getTotal = typeof total === 'number'
      ? Promise.resolve(total)
      : fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) { return c.total_price; });
    getTotal
      .then(function (t) {
        if (live && liveTotal === t) return null; // the theme already re-rendered the drawer
        if (!live && !t) return null;            // empty cart, nothing to show
        return fetch(ROOT + '?sections=cart-drawer', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (json) { if (json && json['cart-drawer']) replaceParts(d, json['cart-drawer']); });
      })
      .catch(function () { /* the theme's own rendering stays in charge */ });
  }

  function scheduleSync(total) {
    // several events arrive for one change (cart:change + pub/sub): keep the known total, run once
    if (typeof total === 'number') pendingTotal = total;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      var t = pendingTotal;
      pendingTotal = null;
      sync(t);
    }, 200);
  }

  document.addEventListener('cart:change', function (e) {
    var cart = e && e.detail && e.detail.cart;
    scheduleSync(cart && typeof cart.total_price === 'number' ? cart.total_price : null);
  });
  document.addEventListener('cart:refresh', function () { scheduleSync(null); });

  /* ---------- init ---------- */
  function init() {
    var d = drawerEl();
    if (!d || d.__ticInit) return;
    d.__ticInit = true;
    scan(d, false); // page load: read the current state, no animation
    new MutationObserver(function () { scan(d, true); }).observe(d, { childList: true, subtree: true });
    var wasOpen = isOpen(d);
    new MutationObserver(function () {
      var open = isOpen(d);
      if (open && !wasOpen) onOpen(d);
      wasOpen = open;
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
    if (subscribed) return;
    subscribed = true;
    try {
      // theme pub/sub (assets/pubsub.js + constants.js): adds from quick-add or other forms
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleSync(null); });
      }
    } catch (err) { /* pub/sub not available */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init); // theme editor re-renders the header
})();
